import { action, observable, computed } from 'mobx';
import { root } from '@estee/elc-universal-utils';
import { IVtoFoundationQrCodeOverlayNodeData } from '~exported//vto-foundation-qr-code-overlay/VtoFoundationQrCodeOverlay';
import { emit, events } from '@estee/elc-service-bus';
import {
    VTO_OPEN_QUERY_PARAM,
    VTO_OPEN_QUERY_VALUE
} from '../constants/VtoFoundationQrCodeOverlay';
import { VtoBaseViewController } from './VtoBaseViewController';
import { VTO_FOUNDATION } from '../constants/VtoFoundationOverlay';

export interface IVtoFoundationQrCodeOverlayViewController {
    nodeData: IVtoFoundationQrCodeOverlayNodeData[];
}

export class VtoFoundationQrCodeOverlayViewController extends VtoBaseViewController {
    @observable public qrCodeUrl: string;
    @observable public isQrCodeVisible: boolean = false;
    @observable public overlayData = {} as IVtoFoundationQrCodeOverlayNodeData;

    constructor() {
        super();
        this.setIsVtoDisclosed();
    }

    @action
    public loaded = async (props: IVtoFoundationQrCodeOverlayViewController) => {
        const { nodeData: [overlayData] = [] } = props;

        this.overlayData = overlayData || this.overlayData;

        this.isLoading = true;

        this.setQrCodeUrl();

        this.setIsQrCodeVisible(false);

        if (this.overlayData.showShadeReminderScreen) {
            await this.setBestMatch();
        }

        this.isLoading = false;
    };

    @computed
    get data() {
        return {
            qrCodeUrl: this.qrCodeUrl,
            isVtoDisclosed: this.isVtoDisclosed,
            isLoading: this.isLoading,
            isBestMatch: this.isBestMatch,
            bestMatch: this.bestMatch,
            title: this.title,
            subtitle: this.subtitle
        };
    }

    public closeOverlay = () => {
        if (this.isBestMatch) {
            this.closeShadeReminderOverlay();
        }

        emit(events.VTO_FOUNDATION_QR_CODE_OVERLAY_CLOSED, { vtoType: VTO_FOUNDATION });
    };

    public selectShade = () => {
        emit(events.PRODUCT_SHADE_SELECTED, { shadeName: this.bestMatch.name });
        this.closeOverlay();
        this.retainShadeReminderOverlay();
    };

    @action
    public setIsQrCodeVisible = (isVisible: boolean) => {
        this.isQrCodeVisible = isVisible;
    };

    public findDifferentShade = () => {
        this.setIsQrCodeVisible(true);
        this.retakeVtoShadeReminder();
    };

    @computed
    get isBestMatch() {
        return !!this.bestMatch && !this.isQrCodeVisible;
    }

    @computed
    get title() {
        const { title, bestMatchTitle } = this.overlayData;

        return this.isBestMatch ? bestMatchTitle : title;
    }

    @computed
    get subtitle() {
        const { subtitle, bestMatchSubtitle } = this.overlayData;

        return this.isBestMatch ? bestMatchSubtitle : subtitle;
    }

    @action
    private setQrCodeUrl() {
        const currentLocation = new URL(root.location.toString());
        currentLocation.search = this.getQueryString(currentLocation);
        this.qrCodeUrl = currentLocation.toString();
    }

    private getQueryString(currentLocation: URL) {
        const search = new URLSearchParams(currentLocation.search);

        const { qrCodeQueryParams } = this.overlayData;

        const qrCodeQuerySearch = qrCodeQueryParams && new URLSearchParams(qrCodeQueryParams);

        const qrCodeParams = new URLSearchParams({
            ...Object.fromEntries(search),
            ...(qrCodeQuerySearch && Object.fromEntries(qrCodeQuerySearch))
        });

        qrCodeParams.set(VTO_OPEN_QUERY_PARAM, VTO_OPEN_QUERY_VALUE);

        return qrCodeParams.toString();
    }
}
